import React, { useEffect, useState } from "react";
import { Plus, Box, Package, Layers, ArrowRight, Edit } from "lucide-react";
import {
  AddFlex,
  CustomInput,
  CustomInputArea,
  CustomInputLabel,
  CustomText,
  DialogHeader,
  PrimaryActionButton,
} from "../reusableStyles";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import useDialogActionsHook from "../CustomHooks.js/useDialogActionsHook";
import { Label } from "../Forms/FormSidebar";
import { TextareaAutosize as Textarea } from "@mui/material";
import { Button, Card, Input } from "antd";
import Meta from "antd/es/card/Meta";
import { callNonStoreApi } from "../NonStoreApis";
import {
  AddNewCategory,
  GetAllCatalogueCategories,
  GetAllProducts,
  UpdateCategory,
  UpdateProduct,
} from "../Api/ApiEndPoints";
import { enqueueSnackbar } from "notistack";
import {
  generateUUID,
  isArrayNullOrUndefined,
  isStringNullOrUndefined,
} from "../utils";
import GalleryComponentWrapper from "../GalleryComponentWrapper";
import DialogWrapper from "../DialogWrapper";
import UserInstaPosts from "../Integrations/Instagram/UserInstaPosts";
import PostsContent from "../Integrations/Instagram/PostsContent";
import useWindowSize from "../Hooks/useWindowSize";
import { primaryActionColor } from "../constants";
import { useSelector } from "react-redux";
import Close from "@mui/icons-material/Close";

const ProductCatalog = () => {
  // Initialize all state at the top of the component
  const { isMobileView } = useWindowSize();
  const { credentials } = useSelector((state) => state.instagram);
  const { openDialog, isDialogOpen, closeDialog } = useDialogActionsHook();
  const [initialLoading, setInitialLoading] = useState(true);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isUpdateingProduct, setIsUpdatingProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [categoryEdit, setCategoryEdit] = useState(false);
  const [updatedCategory, setUpdatedCategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [newCategory, setNewCategory] = useState("");
  const [formData, setFormData] = useState({
    id: generateUUID(),
    productUrl: "",
    name: "",
    description: "",
    imageData: "",
    category: "",
    tags: "",
  });

  // Helper functions
  const handleInputChange = (name, text) => {
    setFormData((prev) => ({
      ...prev,
      [name]: text,
    }));
  };

  const handleAddCategory = async () => {
    try {
      setIsAddingCategory(true);
      if (isStringNullOrUndefined(newCategory.trim())) {
        setIsAddingCategory(false);
        return enqueueSnackbar("Please enter some valid category name", {
          variant: "error",
        });
      }
      // api to add new category
      const response = await callNonStoreApi(AddNewCategory, {
        category: newCategory.trim(),
        instaUserId: credentials.user_id,
        instaId: credentials.id,
      });

      setCategories((prev) => [...prev, newCategory.trim()]);
      setNewCategory("");
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Something went wrong", {
        variant: "error",
      });
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleAddProduct = async () => {
    try {
      setIsUpdatingProduct(true);
      if (
        !formData.name ||
        !formData.description ||
        !formData.imageData ||
        !formData.productUrl
      ) {
        enqueueSnackbar("Please fill in all required fields", {
          variant: "error",
        });
        return;
      }

      const newProduct = {
        ...formData,
        // tags: formData.tags
        //   .split(",")
        //   .map((tag) => tag.trim())
        //   .filter((tag) => tag),
      };

      // api to updateProduct
      const response = await callNonStoreApi(UpdateProduct, {
        ...newProduct,
        instaUserId: credentials.user_id,
        instaId: credentials.id,
      });

      if (products.some((product) => response.data.product.id === product.id)) {
        setProducts((prev) =>
          prev.map((prevProd) =>
            prevProd.id === response.data.product.id
              ? response.data.product
              : prevProd
          )
        );
      } else {
        setProducts((prev) => [response.data.product, ...prev]);
      }

      setFormData({
        id: generateUUID(),
        productUrl: "",
        name: "",
        description: "",
        imageData: {},
        category: "",
        tags: "",
      });
      enqueueSnackbar("Product Updated Successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
      closeDialog();
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Something went wrong", {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  const getCategoryCount = (category) => {
    return category === "All"
      ? products.length
      : products.filter((product) => product.category === category).length;
  };

  const filteredProducts =
    selectedCategory === "All"
      ? products
      : products.filter((product) => product.category === selectedCategory);

  const handleUpdateCategory = async () => {
    try {
      setIsUpdatingCategory(true);
      const body = {
        instaUserId: credentials.user_id,
        instaId: credentials.id,
        oldCategory: selectedCategory,
        newCategory: updatedCategory,
      };
      const response = await callNonStoreApi(UpdateCategory, body);
      setSelectedCategory(updatedCategory);
      setCategories(response.data.categories);
      setProducts(response.data.products);
      setCategoryEdit(false);
      setUpdatedCategory("");
      enqueueSnackbar("Category updated successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Something went wrong", {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleGetInitialData = async () => {
    try {
      // get categories,
      const body = {
        instaUserId: credentials.user_id,
        instaId: credentials.id,
      };
      const categoriesRes = await callNonStoreApi(
        GetAllCatalogueCategories,
        body
      );
      setCategories(categoriesRes.data.categories || []);
      // get products
      const productsRes = await callNonStoreApi(GetAllProducts, body);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Something went wrong", {
        variant: "error",
      });
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    handleGetInitialData();
  }, [credentials]);

  if (initialLoading) {
    return (
      <AddFlex flexDirection="column" alignItems="center" justify="center">
        <CircularProgress sx={14} />
        <CustomText>Please Wait</CustomText>
      </AddFlex>
    );
  }

  // Main view when there are categories or products
  return (
    <>
      <div
        className={`flex ${isMobileView() ? "flex-col" : ""}`}
        style={{
          overflow: "hidden",
          height: "100%",
          padding: isMobileView() ? 0 : "0 48px",
        }}
      >
        {/* Sidebar */}

        {!isMobileView() && (
          <div className="border-r p-4 pl-0">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-4">
                <CustomInput
                  placeholder="Add new category"
                  value={newCategory}
                  onChange={(text) => setNewCategory(text)}
                  className="flex-1"
                />
                <PrimaryActionButton size="sm" onClick={handleAddCategory}>
                  {isAddingCategory ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </PrimaryActionButton>
              </div>
            </div>

            <div className="space-y-1">
              <button
                onClick={() => setSelectedCategory("All")}
                className={`w-full text-left px-4 py-2 rounded-lg flex justify-between items-center ${
                  selectedCategory === "All"
                    ? "bg-blue-100 text-blue-700"
                    : "hover:bg-gray-100"
                }`}
              >
                <span>All Products</span>
                <span className="text-sm text-gray-500">
                  {getCategoryCount("All")}
                </span>
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`w-full text-left px-4 py-2 rounded-lg flex justify-between items-center ${
                    selectedCategory === category
                      ? "bg-blue-100 text-blue-700"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <span>{category}</span>
                  <span className="text-sm text-gray-500">
                    {getCategoryCount(category)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-auto p-6">
          {isMobileView() && (
            <ResponsiveCategory
              setSelectedCategory={setSelectedCategory}
              setNewCategory={setNewCategory}
              newCategory={newCategory}
              handleAddCategory={handleAddCategory}
              getCategoryCount={getCategoryCount}
              categories={categories}
              selectedCategory={selectedCategory}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            {categoryEdit ? (
              <AddFlex gap="5px">
                <Input
                  value={updatedCategory}
                  onChange={(e) => {
                    setUpdatedCategory(e.target.value);
                  }}
                />
                <Button
                  type="primary"
                  style={{ width: "max-content" }}
                  onClick={handleUpdateCategory}
                >
                  {isUpdatingCategory ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : (
                    "Update Category"
                  )}
                </Button>
                <Button
                  style={{ width: "max-content" }}
                  onClick={() => {
                    setCategoryEdit(false);
                    setUpdatedCategory("");
                  }}
                >
                  <Close className="p-1 text-gray-500 hover:text-blue-600 transition-colors" />
                </Button>
              </AddFlex>
            ) : (
              <AddFlex alignItems="center" gap="4px">
                <h1 className="text-xl font-bold">
                  {selectedCategory === "All"
                    ? "All Products"
                    : selectedCategory}
                </h1>
                {selectedCategory !== "All" && (
                  <button
                    onClick={() => {
                      setCategoryEdit(true);
                      setUpdatedCategory(selectedCategory);
                    }}
                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                )}
              </AddFlex>
            )}
            {!isMobileView() && (
              <PrimaryActionButton onClick={openDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add New Product
              </PrimaryActionButton>
            )}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-center">
              <Box className="w-16 h-16 text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Products Found</h2>
              <p className="text-gray-600 mb-6">
                {selectedCategory === "All"
                  ? "Start by adding your first product"
                  : `No products in the ${selectedCategory} category yet`}
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              style={{ overflow: "scroll" }}
            >
              {filteredProducts.map((product) => (
                <Card
                  key={product.id}
                  bordered={true}
                  cover={
                    <img
                      style={{
                        width: "100%",
                        height: "150px",
                        objectFit: "cover",
                      }}
                      src={product.imageData.mediaUrl || product.imageData.src}
                      alt={product.name}
                    />
                  }
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setFormData({
                      ...product,
                      isEdit: true,
                      tags: product.tags || "",
                    });
                    openDialog();
                  }}
                >
                  <Meta
                    title={product.name}
                    description={
                      <CustomText lineClamp={2}>
                        {product.description}
                      </CustomText>
                    }
                  />
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogWrapper
        open={isDialogOpen}
        closeDialog={() => {
          setFormData({
            id: generateUUID(),
            productUrl: "",
            name: "",
            description: "",
            imageData: "",
            category: "",
            tags: "",
          });
          closeDialog();
        }}
        component={
          <AddFlex flexDirection="column" padding="20px">
            <DialogHeader
              mainText={"Add New Product"}
              onClose={() => {
                setFormData({
                  id: generateUUID(),
                  productUrl: "",
                  name: "",
                  description: "",
                  imageData: "",
                  category: "",
                  tags: "",
                });
                closeDialog();
              }}
            ></DialogHeader>
            <ProductForm
              handleInputChange={handleInputChange}
              formData={formData}
              categories={categories}
            />
            <PrimaryActionButton onClick={handleAddProduct}>
              {isUpdateingProduct ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : formData.isEdit ? (
                "Update Product"
              ) : (
                "Add Product"
              )}
            </PrimaryActionButton>
          </AddFlex>
        }
      />

      {isMobileView() && (
        <div
          style={{
            position: "fixed",
            width: "100%",
            bottom: 0,
            left: 0,
            padding: "10px",
            zIndex: 11111111,
            backgroundColor: "white",
          }}
        >
          <PrimaryActionButton
            onClick={openDialog}
            style={{ margin: "0 auto" }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Product
          </PrimaryActionButton>
        </div>
      )}
    </>
  );
};

export default ProductCatalog;

const ProductForm = ({ handleInputChange, categories, formData }) => {
  const { isMobileView } = useWindowSize();
  return (
    <div className="grid gap-4 py-4">
      {/* Form fields here */}
      <div className="grid gap-2">
        <Label htmlFor="name">Product URL *</Label>
        <CustomInput
          id="name"
          name="name"
          value={formData.productUrl}
          onChange={(text) => handleInputChange("productUrl", text)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="name">Name *</Label>
        <CustomInput
          id="name"
          name="name"
          value={formData.name}
          onChange={(text) => handleInputChange("name", text)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="description">Description *</Label>
        <CustomInputArea
          id="description"
          name="description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          required
          minRows={5}
          style={{
            border: "1px solid #ececec",
            resize: "none",
            padding: "5px",
          }}
        />
      </div>
      <div>
        <Label htmlFor="imageUrl">Add Product Image</Label>
        <img
          src={formData.imageData.mediaUrl || formData.imageData.src}
          style={{ width: "100px", height: "100px", marginBottom: "20px" }}
        />
        <AddFlex flexDirection={isMobileView() ? "column" : "row"} gap="10px">
          <ChooseImageFromGalleryButton
            imageData={formData.imageData}
            handleInputChange={handleInputChange}
          />
          <ChooseImageFromInstagram
            imageData={formData.imageData}
            handleInputChange={handleInputChange}
          />
        </AddFlex>
      </div>
      {/* <div className="grid gap-2">
        <Label htmlFor="imageUrl">Image URL *</Label>
        <CustomInput
          id="imageUrl"
          name="imageUrl"
          value={formData.imageUrl}
          onChange={(text) => handleInputChange("imageUrl", text)}
          required
        />
      </div> */}
      <div className="grid gap-2">
        <Label htmlFor="category">Category</Label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={(e) => handleInputChange("category", e.target.value)}
          className="p-2 border rounded-md"
        >
          <option value="">Select Category</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>
      {/* <div className="grid gap-2">
    <Label htmlFor="tags">Tags (comma-separated)</Label>
    <CustomInput
      id="tags"
      name="tags"
      value={formData.tags}
      onChange={handleInputChange}
      placeholder="tag1, tag2, tag3"
    />
  </div> */}
    </div>
  );
};

const ChooseImageFromGalleryButton = ({ imageData, handleInputChange }) => {
  const [files, setFiles] = useState(
    imageData?.type === "gallery" ? [imageData] : []
  );
  const { isDialogOpen, openDialog, closeDialog, setIsDialogOpen } =
    useDialogActionsHook();
  const handleGetFiles = (isMultiple, file) => {
    handleInputChange("imageData", { ...file[0], type: "gallery" });
  };
  return (
    <>
      <AddFlex
        border="1px solid #ececec"
        borderRadius="12px"
        alignItems="center"
        justify="center"
        padding="20px"
        grow={1}
        style={{ cursor: "pointer" }}
        onClick={() => {
          openDialog();
        }}
      >
        <CustomText>Choose from gallery</CustomText>
      </AddFlex>
      <GalleryComponentWrapper
        openGallery={isDialogOpen}
        setOpenGallery={setIsDialogOpen}
        files={files}
        handleGetFiles={handleGetFiles}
        isMultiple={false}
      />
    </>
  );
};

const ChooseImageFromInstagram = ({ imageData, handleInputChange }) => {
  const [mediaData, setMediaData] = useState(
    imageData?.type === "insta" ? imageData : {}
  );
  const { isDialogOpen, openDialog, closeDialog, setIsDialogOpen } =
    useDialogActionsHook();

  useEffect(() => {
    if (Object.values(mediaData).length > 0)
      handleInputChange("imageData", { ...mediaData, type: "insta" });
  }, [mediaData]);
  return (
    <>
      <AddFlex
        border="1px solid #ececec"
        borderRadius="12px"
        alignItems="center"
        justify="center"
        padding="20px"
        grow={1}
        style={{ cursor: "pointer" }}
        onClick={() => {
          openDialog();
        }}
      >
        <CustomText>Choose from instagram</CustomText>
      </AddFlex>
      <DialogWrapper
        open={isDialogOpen}
        closeDialog={closeDialog}
        component={
          <PostsContent
            closeDialog={() => {
              // e.stopPropagation();
              closeDialog();
            }}
            isStory={false}
            mediaData={mediaData}
            setMediaData={setMediaData}
          />
        }
      />
    </>
  );
};

const ResponsiveCategory = ({
  setSelectedCategory,
  setNewCategory,
  newCategory,
  handleAddCategory,
  isAddingCategory,
  getCategoryCount,
  categories,
  selectedCategory,
}) => {
  const [_categories, setCategories] = useState([]);
  useEffect(() => {
    setCategories(categories.slice(0, 4));
  }, [categories]);
  return (
    <AddFlex
      flexDirection="column"
      className="mb-4 gap-2"
      style={{ height: "100%" }}
    >
      <h1 className="text-xl font-bold">Categories</h1>
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <CustomInput
            placeholder="Add new category"
            value={newCategory}
            onChange={(text) => setNewCategory(text)}
            className="flex-1"
          />
          <PrimaryActionButton size="sm" onClick={handleAddCategory}>
            {isAddingCategory ? (
              <CircularProgress size={14} sx={{ color: "white" }} />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </PrimaryActionButton>
        </div>
      </div>
      <AddFlex
        className="gap-2"
        wrap="wrap"
        style={{
          overflow: "hidden",
          height: "100%",
          alignContent: "flex-start",
        }}
      >
        <div
          onClick={() => setSelectedCategory("All")}
          className={`w-full text-left px-4 py-1 rounded-lg flex justify-between items-center ${
            selectedCategory === "All"
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-grey-700"
          }`}
          style={{ width: "max-content", fontSize: "14px" }}
        >
          {`All Products (${getCategoryCount("All")})`}
        </div>
        {_categories.map((category) => (
          <div
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`w-full text-left px-4 py-1 rounded-lg flex justify-between items-center ${
              selectedCategory === category
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-100 text-grey-700"
            }`}
            style={{ width: "max-content", fontSize: "14px" }}
          >
            {`${category} (${getCategoryCount(category)})`}
          </div>
        ))}
        <CustomText
          color={primaryActionColor}
          style={{
            textDecoration: "underline",
            fontSize: "14px",
            marginTop: "2px",
          }}
          onClick={() => {
            if (_categories.length > 4) {
              setCategories(categories.slice(0, 4));
            } else {
              setCategories(categories);
            }
          }}
        >
          {!isArrayNullOrUndefined(categories) &&
            categories.length > 4 &&
            (_categories.length > 4
              ? "Show less"
              : `Show ${categories.length - 4} More`)}
        </CustomText>
      </AddFlex>
    </AddFlex>
  );
};
